import type {
  RiskScoreBreakdown,
  RiskScoreBreakdownComplete,
} from '@risksmart-app/domain/src/types/risk-score-breakdown';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { buildNarrative, type NarrativeTranslator } from './buildNarrative';
import {
  type RiskScoreBreakdownDetails,
  RiskScoreBreakdownPopover as RiskScoreBreakdownPopoverPattern,
} from './RiskScoreBreakdownPopoverView';

export interface RiskScoreBreakdownPopoverProps {
  breakdown: RiskScoreBreakdown;
  /**
   * Optional override for the trigger aria-label. Defaults to the
   * `riskScoreBreakdown.triggerLabel` translation.
   */
  triggerLabel?: string;
}

const formatNumber = (value: number, fractionDigits = 1): string =>
  Number.isInteger(value) ? value.toString() : value.toFixed(fractionDigits);

/**
 * Format a numeric value with up to `maxDigits` decimal places, stripping
 * trailing zeros. Used for config-supplied weights and aggregate averages
 * where the natural precision varies (1, 0.25, 0.75, 2.8, etc.).
 */
const formatPrecise = (value: number, maxDigits = 2): string => {
  if (Number.isInteger(value)) {
    return value.toString();
  }

  return Number(value.toFixed(maxDigits)).toString();
};

const formatAggregateFormula = (
  contributions: RiskScoreBreakdownComplete['controlTypes'],
  weightField: 'likelihoodWeight' | 'impactWeight'
): string | null => {
  const contributing = contributions.filter((entry) => entry[weightField] > 0);
  if (contributing.length === 0) {
    return null;
  }

  const numerator = contributing
    .map(
      (entry) =>
        `${formatPrecise(entry.avgEffectiveness)}×${formatPrecise(entry[weightField])}`
    )
    .join(' + ');
  const denominator = formatPrecise(
    contributing.reduce((sum, entry) => sum + entry[weightField], 0)
  );

  return `(${numerator}) / ${denominator}`;
};

interface DimensionLabels {
  start: string;
  lower: string;
}

interface ResolvedDimensionLabels {
  likelihood: DimensionLabels;
  impact: DimensionLabels;
}

type Translator = (key: string, vars?: Record<string, unknown>) => string;

const buildDimensionFormulaLines = (
  breakdown: RiskScoreBreakdownComplete,
  dimensionName: 'likelihood' | 'impact',
  tFormula: Translator
): string[] => {
  const dimension = breakdown[dimensionName];
  const inherent =
    dimensionName === 'likelihood'
      ? breakdown.inherent.likelihood
      : breakdown.inherent.impact;
  const residual =
    dimensionName === 'likelihood'
      ? breakdown.residual.likelihood
      : breakdown.residual.impact;

  if (dimension.band === null || dimension.aggregateAvg === null) {
    return [tFormula('noContribution', { inherent })];
  }

  const aggregateFormula = formatAggregateFormula(
    breakdown.controlTypes,
    dimensionName === 'likelihood' ? 'likelihoodWeight' : 'impactWeight'
  );
  const reductionPct = Math.round((1 - dimension.multiplier) * 100);
  const reductionAmount = inherent * (1 - dimension.multiplier);

  return [
    tFormula('aggregateLine', {
      formula: aggregateFormula ?? '—',
      result: formatPrecise(dimension.aggregateAvg),
    }),
    tFormula('residualLine', { inherent, percent: reductionPct }),
    `          ${tFormula('residualSubstitution', {
      inherent,
      reduction: formatPrecise(reductionAmount),
      residual,
    })}`,
  ];
};

const buildDetailsForComplete = (
  breakdown: RiskScoreBreakdownComplete,
  tRoot: Translator,
  tFormula: Translator,
  dimensionLabels: ResolvedDimensionLabels
): RiskScoreBreakdownDetails => ({
  controlTypesHeading: tRoot('controlTypesHeading'),
  controlTypes: breakdown.controlTypes.map((entry) => ({
    label: entry.type ?? tRoot('unclassifiedType'),
    count: tRoot('controlCount', { count: entry.count }),
    value: tRoot('avgValue', { value: formatPrecise(entry.avgEffectiveness) }),
  })),
  dimensionsHeading: tRoot('dimensionsHeading'),
  dimensions: (['likelihood', 'impact'] as const).map((name) => {
    const dim = breakdown[name];
    const aggregate =
      dim.aggregateAvg === null
        ? tRoot('avgPrefix', { value: tRoot('emDash') })
        : tRoot('avgPrefix', { value: formatPrecise(dim.aggregateAvg) });
    const reductionPct =
      dim.band === null
        ? tRoot('emDash')
        : `${Math.round((1 - dim.multiplier) * 100)}%`;
    const band =
      dim.band === null
        ? tRoot('bandRange', { lower: tRoot('emDash'), upper: tRoot('emDash') })
        : tRoot('bandRange', {
            lower: formatNumber(dim.band.lowerBound),
            upper: formatNumber(dim.band.upperBound),
          });

    return {
      label: dimensionLabels[name].start,
      aggregate,
      reduction: `${reductionPct} (${band})`,
    };
  }),
  calculation: {
    summaryLabel: tRoot('calculationLabel'),
    sections: [
      {
        heading: dimensionLabels.likelihood.start,
        lines: buildDimensionFormulaLines(breakdown, 'likelihood', tFormula),
      },
      {
        heading: dimensionLabels.impact.start,
        lines: buildDimensionFormulaLines(breakdown, 'impact', tFormula),
      },
      {
        heading: tRoot('residualScoreHeading'),
        lines: [
          tFormula('scoreLine', {
            likelihood: breakdown.residual.likelihood,
            impact: breakdown.residual.impact,
            score: breakdown.residual.score,
          }),
        ],
      },
    ],
  },
});

export const RiskScoreBreakdownPopover: FC<RiskScoreBreakdownPopoverProps> = ({
  breakdown,
  triggerLabel,
}) => {
  // Three scoped translators so each `t()` call resolves a flat key against a
  // shallow keyPrefix. Calling `t('narrative.noContribution')` with the
  // outer 'riskScoreBreakdown' prefix triggers TS2589 because the i18next
  // augmentation has `returnObjects: true` and the keypath inference
  // recurses too deep on dotted paths.
  const { t: tRoot } = useTranslation('common', {
    keyPrefix: 'riskScoreBreakdown',
  });
  const { t: tNarrativeRaw } = useTranslation('common', {
    keyPrefix: 'riskScoreBreakdown.narrative',
  });
  const { t: tFormulaRaw } = useTranslation('common', {
    keyPrefix: 'riskScoreBreakdown.formula',
  });

  // Cast to the loose Translator shape so we can pass these into pure
  // helpers / `buildNarrative` without dragging i18next generics around.
  const tNarrative: NarrativeTranslator =
    tNarrativeRaw as unknown as NarrativeTranslator;
  const tFormula: Translator = tFormulaRaw as unknown as Translator;
  const tRootLoose: Translator = tRoot as unknown as Translator;

  const dimensionLabels: ResolvedDimensionLabels = {
    likelihood: {
      start: tRootLoose('likelihoodLabel'),
      // English-locale assumption: lower-case form is the start-case form
      // lowercased. Translations that need a different lower-case form can
      // expose `likelihoodLabelLower` and we'll wire it up here.
      lower: tRootLoose('likelihoodLabel').toLowerCase(),
    },
    impact: {
      start: tRootLoose('impactLabel'),
      lower: tRootLoose('impactLabel').toLowerCase(),
    },
  };

  const narrative = buildNarrative(breakdown, {
    t: tNarrative,
    dimensionLabels,
  });

  const details =
    breakdown.status === 'complete'
      ? buildDetailsForComplete(
          breakdown,
          tRootLoose,
          tFormula,
          dimensionLabels
        )
      : undefined;

  return (
    <RiskScoreBreakdownPopoverPattern
      triggerLabel={triggerLabel ?? tRootLoose('triggerLabel')}
      paragraphs={narrative.paragraphs}
      details={details}
    />
  );
};
